import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAnimatedNavigate } from '../hooks/useAnimatedNavigate';
import { useAuth } from '../hooks/useAuth';
import { useMobileNav } from '../hooks/useMobileNav';
import { ConversationSidebar } from '../components/ConversationSidebar';
import { MobileNavToggle, SidebarBackdrop } from '../components/MobileNav';
import { ChatContextMenu } from '../components/ChatContextMenu';
import { DarkModeToggle } from '../components/DarkModeToggle';
import { UserAvatarDropdown } from '../components/UserAvatarDropdown';
import { FilesTab } from '../components/projects/FilesTab';
import { ParticipantManagementDialog } from '../components/projects/ParticipantManagementDialog';
import { trpc } from '../api/trpc';

type Tab = 'chats' | 'files';

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const d = new Date(dateStr).getTime();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getInitials(name: string): string {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const animatedNavigate = useAnimatedNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { isMobile, mobileNavOpen, openNav, closeNav } = useMobileNav();
  const [activeTab, setActiveTab] = useState<Tab>('chats');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [showParticipantMgmt, setShowParticipantMgmt] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [editingInstructions, setEditingInstructions] = useState(false);
  const [instructionsDraft, setInstructionsDraft] = useState('');
  const [promptText, setPromptText] = useState('');
  const [contextMenuConvId, setContextMenuConvId] = useState<string | null>(null);
  const [removeConfirmConvId, setRemoveConfirmConvId] = useState<string | null>(null);
  const [changeProjectConvId, setChangeProjectConvId] = useState<string | null>(null);

  const project = trpc.projects.get.useQuery(
    { projectId: projectId! },
    { enabled: !!projectId, refetchOnMount: 'always' },
  );
  const conversations = trpc.projects.listConversations.useQuery(
    { projectId: projectId! },
    { enabled: !!projectId && activeTab === 'chats', refetchOnMount: 'always' },
  );
  const updateProject = trpc.projects.update.useMutation();
  const createConv = trpc.conversations.create.useMutation();
  const sendMessage = trpc.conversations.sendMessage.useMutation();
  const pinMutation = trpc.conversations.pin.useMutation();
  const unpinMutation = trpc.conversations.unpin.useMutation();
  const deleteMutation = trpc.conversations.delete.useMutation();
  const unlinkFromProject = trpc.projects.unlinkConversation.useMutation();
  const archiveProject = trpc.projects.updateStatus.useMutation();
  const linkToProject = trpc.projects.linkConversation.useMutation();
  const utils = trpc.useUtils();

  const { userId } = useAuth();
  const myRole = project.data?.participants.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (p: any) => p.userId === userId
  )?.role;
  const isOwner = myRole === 'owner' || !myRole;

  const handleTitleSave = useCallback(async () => {
    if (!projectId || !titleDraft.trim()) return;
    await updateProject.mutateAsync({ projectId, title: titleDraft.trim() });
    void utils.projects.get.invalidate({ projectId });
    setEditingTitle(false);
  }, [projectId, titleDraft, updateProject, utils]);

  const handleDescSave = useCallback(async () => {
    if (!projectId) return;
    await updateProject.mutateAsync({ projectId, description: descDraft.trim() });
    void utils.projects.get.invalidate({ projectId });
    setEditingDesc(false);
  }, [projectId, descDraft, updateProject, utils]);

  const handleInstructionsSave = useCallback(async () => {
    if (!projectId) return;
    await updateProject.mutateAsync({ projectId, customInstructions: instructionsDraft.trim() });
    void utils.projects.get.invalidate({ projectId });
    setEditingInstructions(false);
  }, [projectId, instructionsDraft, updateProject, utils]);

  const handleSendPrompt = useCallback(async () => {
    if (!promptText.trim() || !projectId) return;
    const content = promptText.trim();
    setPromptText('');
    const conv = await createConv.mutateAsync({ projectId });
    await sendMessage.mutateAsync({ conversationId: conv.id, content, surface: 'web' });
    animatedNavigate(`/?c=${conv.id}`);
  }, [promptText, projectId, createConv, sendMessage, animatedNavigate]);

  const p = project.data;

  return (
    <div className="chat-layout">
      <div className="chat-body">
        <ConversationSidebar
          activeId={null}
          onSelect={(id: string) => { closeNav(); animatedNavigate(`/?c=${id}`); }}
          onNew={() => { closeNav(); animatedNavigate('/'); }}
          onSearch={() => { closeNav(); }}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(c => !c)}
          onNavigateToProjects={() => { closeNav(); animatedNavigate('/projects'); }}
          onNavigateToProject={(id: string) => { closeNav(); animatedNavigate(`/projects/${id}`); }}
          onNavigateToAgents={() => { closeNav(); animatedNavigate('/agents'); }}
          onNavigateToAgent={(id: string) => { closeNav(); animatedNavigate(`/agents/${id}`); }}
          onNavigateHome={() => { closeNav(); animatedNavigate('/'); }}
          isMobile={isMobile}
          mobileOpen={mobileNavOpen}
          onMobileClose={closeNav}
        />
        {isMobile && mobileNavOpen && <SidebarBackdrop onClose={closeNav} />}

        <div className="chat-column">
          <div className="topbar">
            {isMobile && <MobileNavToggle onOpen={openNav} />}
            <div className="topbar-title-area">
              <button
                className="page-back-btn"
                onClick={() => animatedNavigate('/projects')}
                title="Back to projects"
              >
                ←
              </button>
              <span className="topbar-title"></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <DarkModeToggle />
              <UserAvatarDropdown />
            </div>
          </div>

          <main className="chat-main" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="page-scroll">
            <div className="page-inner">
              {/* Header */}
              {p && (
                <div className="proj-header">
                  {editingTitle ? (
                    <input
                      className="proj-title-input"
                      value={titleDraft}
                      onChange={e => setTitleDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') void handleTitleSave(); if (e.key === 'Escape') setEditingTitle(false); }}
                      onBlur={() => void handleTitleSave()}
                      autoFocus
                      maxLength={100}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                      <div
                        className="proj-title-text"
                        onClick={() => { if (isOwner) { setTitleDraft(p.title); setEditingTitle(true); } }}
                        style={{ ...(isOwner ? { cursor: 'pointer' } : {}), marginBottom: 0, flex: 1 }}
                      >
                        {p.title}
                      </div>
                      {isOwner && (
                        <button className="ib" onClick={() => setShowArchiveConfirm(true)} title="Archive project">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}

                  {editingDesc ? (
                    <textarea
                      className="proj-desc-input"
                      value={descDraft}
                      onChange={e => setDescDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Escape') setEditingDesc(false); }}
                      onBlur={() => void handleDescSave()}
                      autoFocus
                      rows={2}
                    />
                  ) : (
                    <div
                      className="proj-desc-text"
                      onClick={() => { setDescDraft(p.description ?? ''); setEditingDesc(true); }}
                      style={{ cursor: 'pointer' }}
                    >
                      {p.description || 'Add a description...'}
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <div style={{ display: 'flex' }}>
                      {p.participants.slice(0, 5).map((part: { user: { id: string; name: string } }) => (
                        <div key={part.user.id} className="proj-card-part" title={part.user.name}>
                          {getInitials(part.user.name)}
                        </div>
                      ))}
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                      {p.participants.length} participant{p.participants.length !== 1 ? 's' : ''} · Created {new Date(p.createdAt as unknown as string).toLocaleDateString()}
                    </span>
                    {isOwner && (
                      <button className="ib" onClick={() => setShowParticipantMgmt(true)} title="Manage participants">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Instructions card */}
              {p && (
                <div className="instructions-card">
                  {editingInstructions ? (
                    <>
                      <textarea
                        className="instructions-edit-area"
                        value={instructionsDraft}
                        onChange={e => setInstructionsDraft(e.target.value)}
                        placeholder="Enter project-specific instructions for Cade..."
                        maxLength={5000}
                      />
                      <div className="instructions-edit-actions">
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditingInstructions(false)}>Cancel</button>
                        <button className="btn btn-primary btn-sm" onClick={() => void handleInstructionsSave()}>Save</button>
                      </div>
                    </>
                  ) : (
                    <div
                      onClick={() => { if (isOwner) { setInstructionsDraft(p.customInstructions ?? ''); setEditingInstructions(true); } }}
                      style={isOwner ? { cursor: 'pointer' } : undefined}
                    >
                      <strong>Project instructions: </strong>
                      {p.customInstructions || (isOwner ? 'Click to add instructions...' : 'No custom instructions set.')}
                    </div>
                  )}
                </div>
              )}

              {/* Tabs */}
              <div className="page-tabs">
                <button
                  className={`page-tab ${activeTab === 'chats' ? 'active' : ''}`}
                  onClick={() => setActiveTab('chats')}
                >
                  Chats
                  {conversations.data && <span className="page-tab-count">{conversations.data.length}</span>}
                </button>
                <button
                  className={`page-tab ${activeTab === 'files' ? 'active' : ''}`}
                  onClick={() => setActiveTab('files')}
                >
                  Files
                </button>
              </div>

              {/* Chats tab */}
              {activeTab === 'chats' && (
                <>
                  {conversations.data?.length === 0 && (
                    <div className="proj-empty">
                      <div className="proj-empty-title">No conversations yet</div>
                      <div className="proj-empty-desc">Use the prompt bar below to start a conversation in this project.</div>
                    </div>
                  )}
                  {conversations.data?.map((conv: { id: string; title?: string | null; updatedAt: unknown; messages: Array<{ content: string }> }) => (
                    <div
                      key={conv.id}
                      className="conv-item"
                      style={{ position: 'relative' }}
                      onClick={() => animatedNavigate(`/?c=${conv.id}`)}
                    >
                      <svg className="conv-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      <span className="conv-text">{conv.title || conv.messages?.[0]?.content?.slice(0, 60) || 'Untitled'}</span>
                      <span className="conv-meta">{formatRelativeTime(conv.updatedAt as string)}</span>
                      <div className={`sb-item-actions ${contextMenuConvId === conv.id ? 'visible' : ''}`}>
                        <button
                          className="sb-item-actions-btn"
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setContextMenuConvId(contextMenuConvId === conv.id ? null : conv.id); }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                          </svg>
                        </button>
                        {contextMenuConvId === conv.id && (
                          <ChatContextMenu
                            isPinned={false}
                            canRename={true}
                            projectId={projectId}
                            projectName={p?.title}
                            onPin={() => { pinMutation.mutate({ conversationId: conv.id }); setContextMenuConvId(null); }}
                            onUnpin={() => { unpinMutation.mutate({ conversationId: conv.id }); setContextMenuConvId(null); }}
                            onRename={() => { animatedNavigate(`/?c=${conv.id}`); }}
                            onDelete={() => { deleteMutation.mutate({ conversationId: conv.id }); setContextMenuConvId(null); void utils.projects.listConversations.invalidate(); }}
                            onChangeProject={() => { setContextMenuConvId(null); setChangeProjectConvId(conv.id); }}
                            onRemoveFromProject={() => { setContextMenuConvId(null); setRemoveConfirmConvId(conv.id); }}
                            onClose={() => setContextMenuConvId(null)}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Files tab */}
              {activeTab === 'files' && projectId && (
                <FilesTab projectId={projectId} />
              )}
            </div>
          </div>

          {/* Prompt bar */}
          {activeTab === 'chats' && (
            <div className="proj-prompt-area">
              <div className="proj-prompt-inner">
                <div className="proj-prompt-bar">
                  <input
                    className="proj-prompt-input"
                    placeholder="Start a new conversation in this project..."
                    value={promptText}
                    onChange={e => setPromptText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSendPrompt(); } }}
                  />
                  <button
                    className="proj-prompt-send"
                    onClick={() => void handleSendPrompt()}
                    disabled={!promptText.trim() || createConv.isPending}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
        </div>
      </div>

      {showParticipantMgmt && p && (
        <ParticipantManagementDialog
          projectId={projectId!}
          participants={p.participants as Array<{ userId: string; role: string; user: { id: string; name: string; email: string } }>}
          onClose={() => setShowParticipantMgmt(false)}
        />
      )}

      {/* Archive project confirmation */}
      {showArchiveConfirm && (
        <div className="delete-dialog-overlay" onClick={() => setShowArchiveConfirm(false)}>
          <div className="delete-dialog" onClick={e => e.stopPropagation()}>
            <div className="delete-dialog-title">Archive {p?.title || 'project'}?</div>
            <div className="delete-dialog-text">
              The project will be moved to the Archived tab. Conversations will remain accessible from your chat list. You can restore it at any time.
            </div>
            <div className="delete-dialog-actions">
              <button className="btn btn-ghost" onClick={() => setShowArchiveConfirm(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={async () => {
                await archiveProject.mutateAsync({ projectId: projectId!, status: 'archived' });
                setShowArchiveConfirm(false);
                animatedNavigate('/projects');
              }}>Archive</button>
            </div>
          </div>
        </div>
      )}

      {/* Remove from project confirmation */}
      {removeConfirmConvId && (
        <div className="delete-dialog-overlay" onClick={() => setRemoveConfirmConvId(null)}>
          <div className="delete-dialog" onClick={e => e.stopPropagation()}>
            <div className="delete-dialog-title">Remove from {p?.title || 'project'}?</div>
            <div className="delete-dialog-text">
              The conversation will be removed from this project but will still be accessible from your chat list.
            </div>
            <div className="delete-dialog-actions">
              <button className="btn btn-ghost" onClick={() => setRemoveConfirmConvId(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => {
                unlinkFromProject.mutate({ conversationId: removeConfirmConvId });
                setRemoveConfirmConvId(null);
                void utils.projects.listConversations.invalidate();
                void utils.projects.get.invalidate({ projectId: projectId! });
              }}>Remove</button>
            </div>
          </div>
        </div>
      )}

      {/* Change project picker */}
      {changeProjectConvId && (
        <ChangeProjectPicker
          currentProjectId={projectId!}
          currentProjectName={p?.title || 'this project'}
          onSelect={async (newProjectId) => {
            await unlinkFromProject.mutateAsync({ conversationId: changeProjectConvId });
            await linkToProject.mutateAsync({ projectId: newProjectId, conversationId: changeProjectConvId });
            setChangeProjectConvId(null);
            void utils.projects.listConversations.invalidate();
            void utils.projects.get.invalidate({ projectId: projectId! });
          }}
          onClose={() => setChangeProjectConvId(null)}
        />
      )}
    </div>
  );
}

/* ── Change project picker ── */
function ChangeProjectPicker({ currentProjectId, currentProjectName, onSelect, onClose }: {
  currentProjectId: string;
  currentProjectName: string;
  onSelect: (projectId: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const { data: projects } = trpc.projects.list.useQuery({});
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    function handleKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filtered = (projects ?? []).filter((p: any) => {
    if (p.id === currentProjectId) return false; // exclude current project
    if (!query.trim()) return true;
    return (p.title ?? '').toLowerCase().includes(query.toLowerCase());
  });

  return (
    <div className="delete-dialog-overlay" onClick={onClose}>
      <div className="proj-dialog" onClick={e => e.stopPropagation()} style={{ width: 360 }}>
        <div className="proj-dialog-title">Change project</div>
        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
          Currently in: <strong>{currentProjectName}</strong>
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search projects..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{
            width: '100%', padding: '8px 12px', marginBottom: 12,
            border: '1px solid var(--color-border)', borderRadius: 'var(--radius)',
            background: 'var(--color-surface)', color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-sans)', fontSize: 13, outline: 'none',
          }}
        />
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          {filtered.length === 0 && (
            <div style={{ padding: '12px 0', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>
              No other projects available
            </div>
          )}
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {filtered.map((proj: any) => (
            <button
              key={proj.id}
              onClick={() => onSelect(proj.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '8px 10px', border: 'none', background: 'none', cursor: 'pointer',
                borderRadius: 'var(--radius)', fontFamily: 'var(--font-sans)', fontSize: 13,
                color: 'var(--color-text-primary)', textAlign: 'left',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg-hover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, color: 'var(--color-text-muted)' }}>
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
              <span>{proj.title}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
